import {
  assertPkceSupported,
  generateCodeChallenge,
  generateCodeVerifier,
  storePKCEValues,
} from './pkce';

/**
 * Shared SSO-initiation primitive used by both the initial login (SignInForm)
 * and the transient-timeout auto-retry (callback, ENG-2910). Centralising the
 * authorize-URL construction here means the two entry points can never build a
 * subtly different request.
 */

/** Default OAuth scopes when the IdP discovery result doesn't specify any. */
export const DEFAULT_SSO_SCOPE = 'openid email profile';

/** Everything needed to (re)build a Cognito /oauth2/authorize redirect. */
export interface SsoRedirectParams {
  authorizeEndpoint: string;
  clientId: string;
  identityProvider?: string;
  scope?: string;
}

/**
 * Assemble the Cognito /oauth2/authorize URL. Pure (no PKCE generation, no
 * navigation) so the exact query string can be unit-tested — this is the
 * highest-value thing to keep stable, since login and retry both depend on it.
 */
export function buildAuthorizeUrl(
  params: SsoRedirectParams,
  redirectUri: string,
  codeChallenge: string,
): string {
  const search = new URLSearchParams({
    response_type: 'code',
    client_id: params.clientId,
    redirect_uri: redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    scope: params.scope || DEFAULT_SSO_SCOPE,
  });
  if (params.identityProvider) {
    search.set('identity_provider', params.identityProvider);
  }
  return `${params.authorizeEndpoint}?${search.toString()}`;
}

/**
 * Generate a fresh PKCE pair, persist the verifier for the callback, and
 * navigate to the authorize URL. Throws via assertPkceSupported if the browser
 * can't perform the OAuth redirect (no Web Crypto / blocked storage); callers
 * handle that — initial login surfaces a "can't start SSO" message, the retry
 * gives up and surfaces the friendly error.
 */
export async function initiateSsoRedirect(
  params: SsoRedirectParams,
  redirectUri: string,
): Promise<void> {
  assertPkceSupported();

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  storePKCEValues(codeVerifier, codeChallenge);

  window.location.href = buildAuthorizeUrl(params, redirectUri, codeChallenge);
}
